'use client';

import { FC, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { useFormContext } from 'react-hook-form';
import { useTranslations } from '@/hooks/useTranslations';
import { getCurrencyFormatter } from '@/lib/currency-formatter';
import { ProductInput } from '@/features/invoices/product-input';
import { TCurrency } from '@/types';
import { TItems } from '@/types/invoice';
import { TProductData } from '@/types/product';

type SubmitStatus = 'idle' | 'loading' | 'success' | 'error';

interface ICreateInvoice {
  status: SubmitStatus;
  items: TItems[];
  setItems: (items: TItems[]) => void;
  currency?: TCurrency;
}

export const Items: FC<ICreateInvoice> = ({
  items,
  setItems,
  status,
  currency,
}) => {
  const t = useTranslations('CreateInvoicePopup');
  const { setValue } = useFormContext();

  useEffect(() => {
    setValue('items', items);
  }, [items, setValue]);

  const addRow = () => {
    setItems([
      ...items,
      {
        description: '',
        qty: 0,
        unit: '',
        price: 0,
        tax: 0,
        total: 0,
      },
    ]);
  };

  const removeRow = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const updateItemField = (
    index: number,
    field: string,
    value: string | number
  ) => {
    const updatedItems = items.map((item, i) => {
      if (i === index) {
        const updatedItem = { ...item, [field]: value };

        if (field === 'qty' || field === 'price') {
          const qty =
            field === 'qty'
              ? typeof value === 'number'
                ? value
                : Number(value)
              : item.qty;
          const price =
            field === 'price'
              ? typeof value === 'number'
                ? value
                : Number(value)
              : item.price;
          updatedItem.total = Number(qty) * Number(price);
        }

        return updatedItem;
      }
      return item;
    });

    setItems(updatedItems);
  };

  const handleProductSelect = (index: number, product: TProductData) => {
    const updatedItems = items.map((item, i) => {
      if (i === index) {
        const updatedItem = {
          ...item,
          description: product.description || product.name,
          price: +product.price,
          unit: product.unit || item.unit,
        };
        updatedItem.total = item.qty * +product.price;
        return updatedItem;
      }
      return item;
    });
    setItems(updatedItems);
  };

  return (
    <div className="flex flex-col gap-5">
      {items.map((item, index) => (
        <div
          key={index}
          className="flex flex-wrap items-start justify-between gap-3 w-full space-x-4"
        >
          <FormField
            name={`items[${index}].description`}
            render={() => (
              <FormItem className="w-full">
                <FormLabel>{t('itemDescription')}</FormLabel>

                <FormControl>
                  <ProductInput
                    value={item.description}
                    onChange={(value) =>
                      updateItemField(index, 'description', value)
                    }
                    onProductSelect={(product) =>
                      handleProductSelect(index, product)
                    }
                    placeholder={t('itemDescription')}
                    disabled={status === 'loading'}
                    currentPrice={item.price}
                  />
                </FormControl>

                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex justify-between gap-2 w-full !ml-0">
            <FormField
              name={`items[${index}].qty`}
              render={({ field }) => (
                <FormItem className="w-1/3">
                  <FormLabel>{t('quantity')}</FormLabel>

                  <FormControl>
                    <Input
                      {...field}
                      type="number"
                      step="0.001"
                      value={item.qty || ''}
                      disabled={status === 'loading'}
                      onChange={(e) => {
                        const value = e.target.valueAsNumber;
                        updateItemField(index, 'qty', isNaN(value) ? 0 : value);
                      }}
                      placeholder={t('quantity')}
                    />
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              name={`items[${index}].price`}
              render={({ field }) => (
                <FormItem className="w-1/3">
                  <FormLabel>{t('price')}</FormLabel>

                  <FormControl>
                    <Input
                      {...field}
                      type="number"
                      step="0.01"
                      value={item.price || ''}
                      onChange={(e) => {
                        const value = e.target.valueAsNumber;
                        updateItemField(
                          index,
                          'price',
                          isNaN(value) ? 0 : value
                        );
                      }}
                      disabled={status === 'loading'}
                      placeholder={t('price')}
                    />
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              name={`items[${index}].total`}
              render={({ field }) => (
                <FormItem className="w-1/3">
                  <FormLabel>{t('total')}</FormLabel>

                  <FormControl>
                    <Input
                      {...field}
                      value={getCurrencyFormatter(item.total, currency)}
                      disabled={true}
                      placeholder={t('total')}
                    />
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="flex justify-between gap-2 w-full !ml-0">
            <FormField
              name={`items[${index}].unit`}
              render={({ field }) => (
                <FormItem className="w-1/2">
                  <FormLabel>{t('unit')}</FormLabel>

                  <FormControl>
                    <Input
                      {...field}
                      value={item.unit}
                      onChange={(e) =>
                        updateItemField(index, 'unit', e.target.value)
                      }
                      disabled={status === 'loading'}
                      placeholder={t('unit')}
                    />
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              name={`items[${index}].tax`}
              render={() => (
                <FormItem className="w-1/2">
                  <FormLabel>{t('tax')}</FormLabel>

                  <FormControl>
                    <Input
                      type="number"
                      step="0.01"
                      value={item.tax || ''}
                      onChange={(e) => {
                        const value = e.target.valueAsNumber;
                        updateItemField(index, 'tax', isNaN(value) ? 0 : value);
                      }}
                      disabled={status === 'loading'}
                      placeholder={t('tax')}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {items.length > 1 && (
            <Button
              aria-label="Delete item"
              type="button"
              variant="destructive"
              className="text-white !ml-0"
              onClick={() => removeRow(index)}
            >
              {t('deleteItem')}
            </Button>
          )}
        </div>
      ))}

      <Button
        type="button"
        className="mt-3 self-start"
        onClick={addRow}
        aria-label="Add item"
      >
        {t('addItem')}
      </Button>
    </div>
  );
};
